import React, { useState, useEffect } from 'react';
import { createProject, getMyCompanies } from '../api';
import UserSelector from './UserSelector';

const emptyForm = {
  title: '', description: '', priority: 'medium',
  start_date: '', end_date: '', assigned_users: [], company_id: '',
};

export default function NewProjectModal({ onClose, onCreated }) {
  const [form,      setForm]      = useState(emptyForm);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    getMyCompanies().then(res => {
      const list = res.data || [];
      setCompanies(list);
      if (list.length === 1) setForm(f => ({ ...f, company_id: list[0].id })); // auto-select the only company
    }).catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title.trim())      { setError('Please enter a project name.'); return; }
    if (companies.length > 1 && !form.company_id) { setError('Please select which company this project belongs to.'); return; }
    if (!form.start_date)        { setError('Start date is required.'); return; }
    if (!form.end_date)          { setError('End date is required.'); return; }
    if (form.end_date < form.start_date) { setError('End date must be after the start date.'); return; }
    if (!form.assigned_users?.length)    { setError('Please assign at least one user.'); return; }

    setSaving(true);
    try {
      const res = await createProject({ ...form, status: 'active', progress: 0 });
      onCreated && onCreated(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create project.');
    } finally { setSaving(false); }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>+ New Project</h2>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={styles.body}>
          {error && <div style={styles.errorBox}>⚠️ {error}</div>}

          <label style={styles.label}>Project Name *</label>
          <input
            style={styles.input}
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="Enter project name"
            autoFocus
          />

          {companies.length > 0 && (
            <>
              <label style={styles.label}>Company {companies.length > 1 ? '*' : ''}</label>
              {companies.length === 1 ? (
                <div style={{ ...styles.input, background: '#f8fafc', color: '#64748b', display: 'flex', alignItems: 'center' }}>
                  🏢 {companies[0].name}
                </div>
              ) : (
                <select style={styles.input} value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })}>
                  <option value="">Select company...</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </>
          )}

          <label style={styles.label}>Description</label>
          <textarea
            style={{ ...styles.input, minHeight: 70, resize: 'vertical' }}
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="What is this project about?"
          />

          <div style={styles.row}>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>Priority</label>
              <select style={styles.input} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>Start Date *</label>
              <input style={styles.input} type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.label}>End Date *</label>
              <input style={styles.input} type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>

          <label style={styles.label}>Assigned Users *</label>
          <UserSelector selected={form.assigned_users} onChange={val => setForm({ ...form, assigned_users: val })} />

          <div style={styles.footer}>
            <button type="button" style={styles.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" style={styles.primaryBtn} disabled={saving}>
              {saving ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal:     { background: 'white', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' },
  header:    { padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title:     { fontSize: 18, fontWeight: 800, margin: 0 },
  closeBtn:  { background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer' },
  body:      { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 4 },
  errorBox:  { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#dc2626' },
  label:     { fontSize: 12, fontWeight: 600, color: '#374151', margin: '10px 0 5px', textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:     { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  row:       { display: 'flex', gap: 12 },
  footer:    { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 },
  cancelBtn: { padding: '10px 20px', border: '1.5px solid #e2e8f0', borderRadius: 9, background: 'white', fontSize: 14, cursor: 'pointer' },
  primaryBtn:{ padding: '10px 22px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
};